import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { FaKey, FaPlus, FaCopy, FaTimes, FaCheck, FaBan, FaTrash, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import apiAxios from 'helpers/apiAxios';
import { toast } from 'react-toastify';

const SCOPES = [
  { key: 'logs:read',   label: 'Read logs (search + history)', desc: 'Query CloudWatch log groups & Insights' },
  { key: 'logs:tail',   label: 'Live tail logs',               desc: 'Stream real-time logs (SSE)' },
  { key: 'system:read', label: 'Read system info',             desc: 'ECS task metadata + process info' },
];

const BASE = '/monitoring/api_keys';

// ── Row ────────────────────────────────────────────────────────────────────────
const KeyRow = ({ k, onRevoke, onDelete }) => {
  const active = k.active;
  return (
    <tr className="hover:tw-bg-slate-50/60 tw-transition-colors">
      <td className="tw-px-3 tw-py-2.5 tw-align-top">
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span className="tw-text-sm tw-font-semibold tw-text-slate-800">{k.name}</span>
          {k.note && <span className="tw-text-[11px] tw-text-slate-500">{k.note}</span>}
        </div>
      </td>
      <td className="tw-px-3 tw-py-2.5 tw-align-top">
        <code className="tw-text-[11px] tw-font-mono tw-bg-slate-100 tw-px-1.5 tw-py-0.5 tw-rounded-sm">{k.masked}</code>
      </td>
      <td className="tw-px-3 tw-py-2.5 tw-align-top">
        <div className="tw-flex tw-flex-wrap tw-gap-1">
          {(k.scopes || []).map(s => (
            <span key={s} className="tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-[10px] tw-font-semibold tw-bg-primary-50 tw-text-primary-700 tw-border tw-border-primary-200">
              {s}
            </span>
          ))}
        </div>
      </td>
      <td className="tw-px-3 tw-py-2.5 tw-align-top tw-text-[11px] tw-text-slate-500">
        {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : <span className="tw-text-slate-400">never</span>}
        {k.last_used_ip && <div className="tw-text-slate-400">{k.last_used_ip}</div>}
      </td>
      <td className="tw-px-3 tw-py-2.5 tw-align-top tw-text-[11px] tw-text-slate-500">
        {k.expires_at ? new Date(k.expires_at).toLocaleDateString() : <span className="tw-text-slate-400">—</span>}
      </td>
      <td className="tw-px-3 tw-py-2.5 tw-align-top">
        {active
          ? <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-sm tw-text-[10px] tw-font-semibold tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-emerald-200"><FaCheck size={8} /> Active</span>
          : <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-sm tw-text-[10px] tw-font-semibold tw-bg-rose-50 tw-text-rose-700 tw-border tw-border-rose-200"><FaBan size={8} /> {k.revoked_at ? 'Revoked' : 'Expired'}</span>}
      </td>
      <td className="tw-px-3 tw-py-2.5 tw-align-top tw-text-right">
        {active && (
          <button type="button" onClick={() => onRevoke(k)}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-amber-700 tw-border tw-border-amber-200 tw-rounded-sm hover:tw-bg-amber-50 tw-bg-white tw-cursor-pointer tw-mr-1 tw-border-solid">
            <FaBan size={9} /> Revoke
          </button>
        )}
        <button type="button" onClick={() => onDelete(k)}
          className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-rose-700 tw-border tw-border-rose-200 tw-rounded-sm hover:tw-bg-rose-50 tw-bg-white tw-cursor-pointer tw-border-solid">
          <FaTrash size={9} /> Delete
        </button>
      </td>
    </tr>
  );
};
KeyRow.propTypes = {
  k: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    active: PropTypes.bool,
    name: PropTypes.string,
    note: PropTypes.string,
    masked: PropTypes.string,
    scopes: PropTypes.arrayOf(PropTypes.string),
    last_used_at: PropTypes.string,
    last_used_ip: PropTypes.string,
    expires_at: PropTypes.string,
    revoked_at: PropTypes.string,
  }).isRequired,
  onRevoke: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ── Create Modal ───────────────────────────────────────────────────────────────
const CreateModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [expires, setExpires] = useState('');
  const [scopes, setScopes] = useState(['logs:read', 'logs:tail', 'system:read']);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // { token, ... } after success
  const [copied, setCopied] = useState(false);

  const toggleScope = (s) => setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const handleCreate = async () => {
    if (!name.trim()) return toast.error('Cần nhập name');
    if (!scopes.length) return toast.error('Chọn ít nhất 1 scope');
    setSaving(true);
    try {
      const payload = { api_key: { name: name.trim(), note: note.trim() || null, scopes } };
      if (expires) payload.api_key.expires_at = new Date(expires).toISOString();
      const res = await apiAxios.post(BASE, payload);
      setCreated(res.data.data);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.errors?.join(', ') || e.message);
    } finally {
      setSaving(false);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copy failed'); }
  };

  // Rendered through a portal on <body> with a z-index above the app chrome
  // (sidebar 1019, top navbar 1020, theme customizer 10000) — same convention
  // as GlobalSearchModal. Keeping it inline at tw-z-50 left the backdrop
  // underneath the sidebar/header.
  const modal = (
    <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-p-4"
      style={{ zIndex: 99999 }}>
      <div className="tw-bg-white tw-rounded-lg tw-shadow-2xl tw-w-full tw-max-w-lg tw-overflow-hidden">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-slate-200">
          <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-flex tw-items-center tw-gap-2">
            <FaKey size={12} className="tw-text-primary-500" />
            {created ? 'API Key Created' : 'Create API Key'}
          </h3>
          <button type="button" onClick={onClose} className="tw-w-7 tw-h-7 tw-rounded-sm tw-flex tw-items-center tw-justify-center hover:tw-bg-slate-100 tw-cursor-pointer tw-bg-white tw-border-0"><FaTimes size={11} className="tw-text-slate-500" /></button>
        </div>

        {created ? (
          <div className="tw-p-5 tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-rounded tw-bg-amber-50 tw-border tw-border-amber-200">
              <FaExclamationTriangle className="tw-text-amber-500 tw-shrink-0 tw-mt-0.5" size={16} />
              <div className="tw-text-xs tw-text-amber-800">
                <strong>Copy token này ngay bây giờ.</strong> Đây là lần duy nhất token hiển thị — sau khi đóng dialog, chỉ còn phần mask.
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">Plaintext Token</span>
              <div className="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-rounded tw-border tw-border-slate-200 tw-bg-slate-50">
                <code className="tw-flex-1 tw-text-xs tw-font-mono tw-text-slate-800 tw-break-all">{created.token}</code>
                <button type="button" onClick={copyToken}
                  className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-sm tw-border tw-cursor-pointer tw-border-solid ${copied ? 'tw-bg-emerald-500 tw-text-white tw-border-emerald-500' : 'tw-bg-white tw-text-slate-700 tw-border-slate-300 hover:tw-bg-slate-50'}`}>
                  {copied ? <><FaCheck size={9} /> Copied</> : <><FaCopy size={9} /> Copy</>}
                </button>
              </div>
            </div>

            <div className="tw-text-[11px] tw-text-slate-500">
              Scopes: {created.scopes?.join(', ')} · Prefix: <code className="tw-font-mono">{created.key_prefix}</code>
            </div>
            <button type="button" onClick={onClose}
              className="tw-mt-2 tw-w-full tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-bg-primary-600 tw-rounded hover:tw-bg-primary-700 tw-cursor-pointer tw-border-0">
              Done
            </button>
          </div>
        ) : (
          <div className="tw-p-5 tw-flex tw-flex-col tw-gap-4">
            <label className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">Name *</span>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="project-cli-anhld"
                className="tw-px-3 tw-py-2 tw-text-sm tw-rounded tw-border tw-border-slate-200 focus:tw-border-primary-400 focus:tw-ring-2 focus:tw-ring-primary-100 tw-outline-none" />
            </label>
            <label className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">Note</span>
              <input value={note} onChange={e => setNote(e.target.value)}
                placeholder="Optional description"
                className="tw-px-3 tw-py-2 tw-text-sm tw-rounded tw-border tw-border-slate-200 focus:tw-border-primary-400 focus:tw-ring-2 focus:tw-ring-primary-100 tw-outline-none" />
            </label>
            <label className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">Expires at</span>
              <input type="datetime-local" value={expires} onChange={e => setExpires(e.target.value)}
                className="tw-px-3 tw-py-2 tw-text-sm tw-rounded tw-border tw-border-slate-200 focus:tw-border-primary-400 focus:tw-ring-2 focus:tw-ring-primary-100 tw-outline-none" />
            </label>
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500">Scopes *</span>
              {SCOPES.map(s => (
                <label key={s.key} className="tw-flex tw-items-start tw-gap-2 tw-p-2 tw-rounded tw-border tw-border-slate-200 tw-cursor-pointer hover:tw-bg-slate-50">
                  <input type="checkbox" checked={scopes.includes(s.key)} onChange={() => toggleScope(s.key)}
                    className="tw-mt-0.5" />
                  <div className="tw-flex tw-flex-col">
                    <span className="tw-text-xs tw-font-semibold tw-text-slate-800">{s.label} <code className="tw-font-mono tw-text-[10px] tw-text-slate-500">({s.key})</code></span>
                    <span className="tw-text-[11px] tw-text-slate-500">{s.desc}</span>
                  </div>
                </label>
              ))}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-2">
              <button type="button" onClick={onClose}
                className="tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 tw-bg-white tw-border tw-border-slate-300 tw-rounded hover:tw-bg-slate-50 tw-cursor-pointer tw-border-solid">
                Cancel
              </button>
              <button type="button" onClick={handleCreate} disabled={saving}
                className="tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-bg-primary-600 tw-rounded hover:tw-bg-primary-700 tw-cursor-pointer tw-border-0 disabled:tw-opacity-50">
                {saving ? 'Creating…' : 'Create API Key'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

// ── Main ───────────────────────────────────────────────────────────────────────
const ApiKeysManager = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiAxios.get(BASE);
      setKeys(res.data.data || []);
    } catch (e) {
      toast.error('Load failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"?`)) return;
    try {
      await apiAxios.post(`${BASE}/${k.id}/revoke`);
      toast.success('Revoked');
      load();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };

  const handleDelete = async (k) => {
    if (!window.confirm(`Xoá vĩnh viễn key "${k.name}"? Không phục hồi được.`)) return;
    try {
      await apiAxios.delete(`${BASE}/${k.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };

  const active = useMemo(() => keys.filter(k => k.active).length, [keys]);

  return (
    <div className="tw-p-5 tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-items-center tw-justify-between">
        <div>
          <h2 className="tw-text-base tw-font-bold tw-text-slate-800 tw-flex tw-items-center tw-gap-2">
            <FaKey size={13} className="tw-text-primary-500" /> API Keys
          </h2>
          <p className="tw-text-xs tw-text-slate-500 tw-mt-0.5">
            Cấp key cho Project-CLI / công cụ ngoài để đọc logs & system info qua <code className="tw-font-mono">/api/external/monitoring/*</code>. {active} active.
          </p>
        </div>
        <div className="tw-flex tw-gap-2">
          <button type="button" onClick={load} disabled={loading}
            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-slate-600 tw-border tw-border-slate-200 tw-rounded hover:tw-bg-slate-50 tw-bg-white tw-cursor-pointer tw-border-solid">
            <FaSync size={11} className={loading ? 'tw-animate-spin' : ''} /> Refresh
          </button>
          <button type="button" onClick={() => setCreateOpen(true)}
            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-bg-primary-600 tw-rounded hover:tw-bg-primary-700 tw-cursor-pointer tw-border-0">
            <FaPlus size={10} /> New API Key
          </button>
        </div>
      </div>

      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-text-xs">
            <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
              <tr>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-slate-600 tw-uppercase tw-tracking-wider tw-text-[10px]">Name</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-slate-600 tw-uppercase tw-tracking-wider tw-text-[10px]">Masked</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-slate-600 tw-uppercase tw-tracking-wider tw-text-[10px]">Scopes</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-slate-600 tw-uppercase tw-tracking-wider tw-text-[10px]">Last used</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-slate-600 tw-uppercase tw-tracking-wider tw-text-[10px]">Expires</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-slate-600 tw-uppercase tw-tracking-wider tw-text-[10px]">Status</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-slate-600 tw-uppercase tw-tracking-wider tw-text-[10px]">Actions</th>
              </tr>
            </thead>
            <tbody className="tw-divide-y tw-divide-slate-100">
              {loading && !keys.length ? (
                <tr><td colSpan={7} className="tw-py-8 tw-text-center tw-text-sm tw-text-slate-400">Loading…</td></tr>
              ) : !keys.length ? (
                <tr><td colSpan={7} className="tw-py-10 tw-text-center tw-text-sm tw-text-slate-400">Chưa có API key. Bấm <b>New API Key</b> để tạo.</td></tr>
              ) : (
                keys.map(k => <KeyRow key={k.id} k={k} onRevoke={handleRevoke} onDelete={handleDelete} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      {createOpen && <CreateModal onClose={() => setCreateOpen(false)} onCreated={load} />}
    </div>
  );
};

export default ApiKeysManager;
